import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDate, fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { useAppDispatcher } from 'src/redux/store';
import { AuthPermissions } from 'src/auth/permissions';
import {
  deleteLoanPayment,
  deleteLoanAgreement,
  getLoanAgreementById,
} from 'src/redux/slices/loan-agreement';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import {
  ILoanPayment,
  LoanBorrowerType,
  loanBorrowerName,
  ILoanAgreementItem,
  loanBorrowerIdLine,
  LoanAgreementStatus,
} from 'src/types/loan-agreement';

import LoanPaymentDialog from './loan-payment-dialog';
import LoanBadDebtDialog from './loan-bad-debt-dialog';
import LoanAgreementActivityLog from './loan-agreement-activity-log';
import { planDescription, LOAN_STATUS_LABEL, INSTALLMENT_STATUS_LABEL } from './utils';

type Props = {
  agreement: ILoanAgreementItem;
};

function userName(user?: { firstName?: string; lastName?: string; email?: string }) {
  if (!user) return '-';
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '-';
}

function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" color={color}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function LoanAgreementDetails({ agreement }: Props) {
  const router = useRouter();

  const dispatch = useAppDispatcher();

  const { user: authUser } = useAuthContext();

  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const canAddPayment =
    hasPermission(
      authUser,
      'any',
      AuthPermissions.CREATE_LOAN_PAYMENT,
      AuthPermissions.ADMIN_PERMISSION,
      AuthPermissions.SUPER_ADMIN_PERMISSION
    ) &&
    agreement.summary.remainingInCents > 0 &&
    (agreement.status === LoanAgreementStatus.ACTIVE ||
      (agreement.status === LoanAgreementStatus.BAD_DEBT && isAdmin));

  const canReadAudit = hasPermission(
    authUser,
    'any',
    AuthPermissions.READ_AUDIT,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const paymentDialog = useBoolean();
  const [editingPayment, setEditingPayment] = useState<ILoanPayment | null>(null);
  const [deletingPayment, setDeletingPayment] = useState<ILoanPayment | null>(null);
  const [badDebtMode, setBadDebtMode] = useState<'mark' | 'reverse' | null>(null);
  const deleteAgreementDialog = useBoolean();
  const actionsPopover = usePopover();

  // Bumped after every write so the activity log refetches.
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => {
    dispatch(getLoanAgreementById({ id: agreement._id }));
    setRefreshKey((key) => key + 1);
  };

  const { summary } = agreement;
  const statusLabel = LOAN_STATUS_LABEL[agreement.status];
  const isEmployee = agreement.borrowerType === LoanBorrowerType.EMPLOYEE;

  const handleDeletePayment = async () => {
    if (!deletingPayment) return;
    try {
      await dispatch(
        deleteLoanPayment({ id: agreement._id, paymentId: deletingPayment._id })
      ).unwrap();
      enqueueSnackbar('Payment deleted');
      refresh();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message, { variant: 'error' });
    } finally {
      setDeletingPayment(null);
    }
  };

  const handleDeleteAgreement = async () => {
    try {
      await dispatch(deleteLoanAgreement({ id: agreement._id })).unwrap();
      enqueueSnackbar('Loan agreement deleted');
      router.push(paths.dashboard.loans.root(agreement.status));
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message, { variant: 'error' });
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5">{loanBorrowerName(agreement)}</Typography>
                <Label variant="soft" color={isEmployee ? 'primary' : 'secondary'}>
                  {isEmployee ? 'Employee' : 'Other'}
                </Label>
                <Label variant="filled" color={statusLabel.color}>
                  {statusLabel.label}
                </Label>
                {summary.isOverdue && agreement.status !== LoanAgreementStatus.SETTLED && (
                  <Label variant="soft" color="warning">
                    Overdue · {fCurrencyRupees(summary.overdueAmountInCents)}
                  </Label>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {isEmployee && agreement.employee ? (
                  <Link
                    component={RouterLink}
                    href={paths.dashboard.employees.view(agreement.employee._id)}
                  >
                    {loanBorrowerIdLine(agreement)}
                  </Link>
                ) : (
                  loanBorrowerIdLine(agreement)
                )}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              {canAddPayment && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={() => {
                    setEditingPayment(null);
                    paymentDialog.onTrue();
                  }}
                >
                  Add Installment
                </Button>
              )}

              {isAdmin && (
                <Tooltip title="Admin actions">
                  <IconButton onClick={actionsPopover.onOpen}>
                    <Iconify icon="eva:more-vertical-fill" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{fCurrencyRupees(summary.paidInCents)}</strong> paid of{' '}
                {fCurrencyRupees(summary.capitalInCents)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {summary.progressPercent}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={summary.progressPercent}
              color={
                // eslint-disable-next-line no-nested-ternary
                agreement.status === LoanAgreementStatus.SETTLED
                  ? 'success'
                  : summary.isOverdue
                  ? 'warning'
                  : 'primary'
              }
              sx={{ height: 10, borderRadius: 1 }}
            />
          </Box>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid xs={6} md={3}>
              <Stat label="Loan capital" value={fCurrencyRupees(summary.capitalInCents)} />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat
                label="Paid"
                value={fCurrencyRupees(summary.paidInCents)}
                color="success.main"
              />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat
                label="Remaining"
                value={fCurrencyRupees(summary.remainingInCents)}
                color={summary.remainingInCents > 0 ? 'warning.main' : undefined}
              />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat
                label="Behind schedule"
                value={fCurrencyRupees(summary.overdueAmountInCents)}
                color={summary.isOverdue ? 'error.main' : undefined}
              />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat
                label="Next installment"
                value={
                  summary.nextDue
                    ? `${fDate(summary.nextDue.dueDate)} · ${fCurrencyRupees(
                        summary.nextDue.amountInCents
                      )}`
                    : '-'
                }
              />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat label="Loan paid on" value={fDate(agreement.loanPaidDate)} />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat label="Settlement date" value={fDate(agreement.settlementDate)} />
            </Grid>
            <Grid xs={6} md={3}>
              <Stat label="Approved by" value={agreement.approvedBy} />
            </Grid>
            {agreement.settledAt && (
              <Grid xs={6} md={3}>
                <Stat label="Settled on" value={fDate(agreement.settledAt)} />
              </Grid>
            )}
            <Grid xs={12} md={agreement.settledAt ? 9 : 12}>
              <Stat label="Agreement" value={planDescription(agreement)} />
            </Grid>
          </Grid>

          {(agreement.badDebt || agreement.badDebtReversal || agreement.badDebtOverride) && (
            <Stack spacing={1.5} sx={{ mt: 3 }}>
              {agreement.badDebt && (
                <Alert severity="error">
                  Marked as bad debt by {userName(agreement.badDebt.markedBy)} on{' '}
                  {fDate(agreement.badDebt.markedAt)} — {agreement.badDebt.reason}
                </Alert>
              )}
              {agreement.badDebtReversal && !agreement.badDebt && (
                <Alert severity="info">
                  A bad-debt mark was reversed by {userName(agreement.badDebtReversal.reversedBy)}{' '}
                  on {fDate(agreement.badDebtReversal.reversedAt)} —{' '}
                  {agreement.badDebtReversal.reason}
                </Alert>
              )}
              {agreement.badDebtOverride && (
                <Alert severity="warning">
                  Created while this person had a bad-debt agreement — acknowledged by{' '}
                  {userName(agreement.badDebtOverride.acknowledgedBy)} on{' '}
                  {fDate(agreement.badDebtOverride.acknowledgedAt)}.
                </Alert>
              )}
            </Stack>
          )}
        </Card>
      </Grid>

      <Grid xs={12} md={6}>
        <Card>
          <CardHeader title="Installment Plan" subheader={planDescription(agreement)} />
          <TableContainer sx={{ mt: 2 }}>
            <Scrollbar>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.installments.map((row) => (
                    <TableRow key={row.number}>
                      <TableCell>{row.number}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(row.dueDate)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {fCurrencyRupees(row.amountInCents)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {fCurrencyRupees(row.paidInCents)}
                      </TableCell>
                      <TableCell>
                        <Label variant="soft" color={INSTALLMENT_STATUS_LABEL[row.status].color}>
                          {INSTALLMENT_STATUS_LABEL[row.status].label}
                        </Label>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {fCurrencyRupees(summary.capitalInCents)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {fCurrencyRupees(summary.paidInCents)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
            Payments are applied to the oldest installment first.
          </Typography>
        </Card>
      </Grid>

      <Grid xs={12} md={6}>
        <Card>
          <CardHeader
            title="Payments Received"
            subheader={`${agreement.payments.length} payments`}
          />
          <TableContainer sx={{ mt: 2 }}>
            <Scrollbar>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Recorded by</TableCell>
                    {isAdmin && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agreement.payments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No payments yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {agreement.payments.map((payment) => (
                    <TableRow key={payment._id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fDate(payment.paymentDate)}
                        {payment.note && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {payment.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {fCurrencyRupees(payment.amountInCents)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={fDateTime(payment.createdAt)}>
                          <span>{userName(payment.createdBy)}</span>
                        </Tooltip>
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingPayment(payment);
                                paymentDialog.onTrue();
                              }}
                            >
                              <Iconify icon="solar:pen-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeletingPayment(payment)}
                            >
                              <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
            <Typography variant="subtitle2">Remaining</Typography>
            <Typography variant="subtitle2">{fCurrencyRupees(summary.remainingInCents)}</Typography>
          </Stack>
        </Card>
      </Grid>

      {canReadAudit && (
        <Grid xs={12}>
          <LoanAgreementActivityLog agreementId={agreement._id} refreshKey={refreshKey} />
        </Grid>
      )}

      <CustomPopover
        open={actionsPopover.open}
        onClose={actionsPopover.onClose}
        arrow="right-top"
        sx={{ width: 200 }}
      >
        <MenuItem
          onClick={() => {
            actionsPopover.onClose();
            router.push(paths.dashboard.loans.edit(agreement._id));
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Edit
        </MenuItem>

        {agreement.status === LoanAgreementStatus.ACTIVE && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              actionsPopover.onClose();
              setBadDebtMode('mark');
            }}
          >
            <Iconify icon="solar:danger-triangle-bold" />
            Mark as Bad Debt
          </MenuItem>
        )}

        {agreement.status === LoanAgreementStatus.BAD_DEBT && (
          <MenuItem
            onClick={() => {
              actionsPopover.onClose();
              setBadDebtMode('reverse');
            }}
          >
            <Iconify icon="solar:restart-bold" />
            Reverse Bad Debt
          </MenuItem>
        )}

        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            actionsPopover.onClose();
            deleteAgreementDialog.onTrue();
          }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>
      </CustomPopover>

      <LoanPaymentDialog
        agreement={agreement}
        payment={editingPayment}
        isAdmin={isAdmin}
        open={paymentDialog.value}
        onClose={paymentDialog.onFalse}
        onSuccess={refresh}
      />

      <LoanBadDebtDialog
        agreementId={agreement._id}
        mode={badDebtMode ?? 'mark'}
        open={!!badDebtMode}
        onClose={() => {
          setBadDebtMode(null);
          setRefreshKey((key) => key + 1);
        }}
      />

      <ConfirmDialog
        open={!!deletingPayment}
        onClose={() => setDeletingPayment(null)}
        title="Delete Payment"
        content={
          deletingPayment
            ? `Delete the ${fCurrencyRupees(deletingPayment.amountInCents)} payment from ${fDate(
                deletingPayment.paymentDate
              )}? The remaining balance goes up by that amount.`
            : ''
        }
        action={
          <Button variant="contained" color="error" onClick={handleDeletePayment}>
            Delete
          </Button>
        }
      />

      <ConfirmDialog
        open={deleteAgreementDialog.value}
        onClose={deleteAgreementDialog.onFalse}
        title="Delete Loan Agreement"
        content="The agreement will be removed from every tab. Its history stays in the audit log."
        action={
          <Button variant="contained" color="error" onClick={handleDeleteAgreement}>
            Delete
          </Button>
        }
      />
    </Grid>
  );
}
